import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useDeleteSolarEstimationMutation,
  useLazyDuplicateEstimationQuery,
  useLazyViewSolarEstimationsAdminQuery,
  useLazyViewSolarEstimationsQuery,
  useLazyViewSolarEstimationSummaryQuery,
} from "../../../redux/api/solar-estimation/solarEstimationAPI";
import { selectProfile } from "../../../redux/features/user/user-slice";
import { ViewSolarEstimationsRequestDTO } from "../../../shared/oversight-core/dtos/request-dtos/view-solar-estimations-request-dto";
import { IViewSolarEstimationSummaryResponseDTO } from "../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-summary-response-dto";
import { ViewSolarEstimationsResponseDTO } from "../../../shared/oversight-core/dtos/response-dtos/view-solar-estimations-response-dto";
import { EstimationStatus } from "../../../shared/oversight-core/enums/estimation-status";
import { EEstimationType } from "../../../shared/oversight-core/enums/estimation-type";
import { ESolarSolutionType } from "../../../shared/oversight-core/enums/sola-solution-type";
import { ESolarSimLocalStorage } from "../../../shared/oversight-core/enums/solar-sim-local-storage";
import { UserRole } from "../../../shared/oversight-core/enums/user-roles";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import { ISolarEstimationListView } from "../../../shared/oversight-core/interfaces/solar-estimation-list-view";
import AppDropDown from "../../../shared/oversight-core/ui-elements/app-dropdown/app-drop-down";
import AppSelect, {
  Option,
} from "../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppButton from "../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import ButtonWithBadge from "../../../shared/oversight-core/ui-elements/buttons/button-with-badge/button-with-badge";
import DataTable from "../../../shared/oversight-core/ui-elements/data-table/data-table";
import MaterialIcon, {
  MtIcon,
} from "../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Pagination from "../../../shared/oversight-core/ui-elements/pagination/pagination";
import Search from "../../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  getStatusColor,
  getStatusNameAdmin,
  getStatusNameUser,
} from "../../../shared/oversight-core/utils/estimation-status";
import { getSolarEstimationTypeIcon } from "../../../shared/oversight-core/utils/get-solar-estimation-type-icon";
import Helper from "../../../shared/oversight-core/utils/helpers";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/oversight-core/utils/toast";
import AdminEstimations from "./admin-estimations/admin-estimations";
import DuplicateEstimationModal from "./components/duplicate-estimation-modal/duplicate-estimation-modal";
import EstimationDeleteModal from "./components/estimation-delete-modal/estimation-delete-modal";
import EstimationFilterSidePanel from "./components/estimation-filter-side-panel/estimation-filter-side-panel";
import EstimationSummaryExportModal from "./components/estimation-summary-export-modal/estimation-summary-export-modal";
import EstimationSummary from "./components/estimation-summary-view-card/estimation-summary-view-card";
import EstimationTimelineModal from "./components/estimation-timeline-modal/estimation-timeline-modal";
import PurchaseHistoryModal from "./components/purchase-history-modal/purchase-history-modal";

const estimateOptions: Option[] = [
  { value: "", label: "All Estimation" },
  {
    value: EstimationStatus.SUBMITTED,
    label: "Pending",
  },
  { value: EstimationStatus.APPROVED, label: "Approved" },
  {
    value: EstimationStatus.APPROVED_WITH_CHANGES,
    label: "Approved with Changes",
  },
  { value: EstimationStatus.REJECTED, label: "Rejected" },
  { value: EstimationStatus.PURCHASED, label: "Purchased" },
  { value: EstimationStatus.DELAYED, label: "Delayed" },
  { value: EstimationStatus.MY_ESTIMATIONS, label: "My Estimations" },
];

const initialFilters: ViewSolarEstimationsRequestDTO = {
  searchValue: "",
  from: "",
  to: "",
  pageNumber: 0,
  pageSize: 20,
};

const Estimations = () => {
  const profile = useSelector(selectProfile);

  const [selectedEstimate, setSelectedEstimate] = useState<Option>(
    estimateOptions[0]
  );
  const [searchText, setSearchText] = useState("");
  const [estimations, setEstimations] = useState<ISolarEstimationListView[]>(
    []
  );
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState({
    ...initialFilters,
  });
  const [updateCurrentState, setUpdateCurrentState] = useState(false);
  const [showDeleteSolarEstimationModal, setShowDeleteSolarEstimationModal] =
    useState(false);
  const [estimationId, setEstimationId] = useState("");
  const [
    showDuplicateSolarEstimationModal,
    setShowDuplicateSolarEstimationModal,
  ] = useState(false);
  const [showEstimationFilterSidePanel, setShowEstimationFilterSidePanel] =
    useState(false);
  const [showEstimationTimelineModal, setShowEstimationTimelineModal] =
    useState(false);
  const [
    showEstimationSummaryExportModal,
    setShowEstimationSummaryExportModal,
  ] = useState(false);
  const [showPurchaseHistoryModal, setShowPurchaseHistoryModal] =
    useState(false);
  const [estimationSummary, setEstimationSummary] =
    useState<IViewSolarEstimationSummaryResponseDTO>(
      {} as IViewSolarEstimationSummaryResponseDTO
    );

  const navigate = useNavigate();

  const [triggerSolarEstimations, { isFetching: isFetchingSolarEstimation }] =
    useLazyViewSolarEstimationsQuery();
  const [
    triggerSolarEstimationsAdmin,
    { isFetching: isFetchingSolarEstimationAdmin },
  ] = useLazyViewSolarEstimationsAdminQuery();
  const [
    triggerDeleteSolarEstimation,
    { isLoading: isLoadingDeleteSolarEstimation },
  ] = useDeleteSolarEstimationMutation();
  const [
    triggerSolarEstimationSummary,
    { isFetching: isFetchingSolarEstimationSummary },
  ] = useLazyViewSolarEstimationSummaryQuery();
  const [
    triggerDuplicateEstimation,
    { isFetching: isFetchingDuplicateEstimation },
  ] = useLazyDuplicateEstimationQuery();

  useEffect(() => {
    if (profile?.role) {
      const triggerEstimation =
        profile?.role === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN
          ? triggerSolarEstimationsAdmin
          : triggerSolarEstimations;

      const requestParams: ViewSolarEstimationsRequestDTO = {
        pageNumber: filters.pageNumber,
        pageSize: filters.pageSize,
        searchValue: filters.searchValue,
        ascending: true,
      };

      if (
        selectedEstimate.value &&
        selectedEstimate.value !== EstimationStatus.MY_ESTIMATIONS
      ) {
        requestParams.status = selectedEstimate.value as EstimationStatus;
      }

      if (profile?.role === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN) {
        requestParams.adminEstimations = false;
      }

      if (filters.from && filters.to) {
        requestParams.from = filters.from;
        requestParams.to = filters.to;
      }

      triggerEstimation({
        ...requestParams,
      })
        .unwrap()
        .then((res: ViewSolarEstimationsResponseDTO) => {
          setTotalElements(res.page.totalElements);
          setEstimations(res.page.elements);
        })
        .catch(() => {
          setEstimations([]);
        });
    }
  }, [
    triggerSolarEstimations,
    triggerSolarEstimationsAdmin,
    profile?.role,
    filters,
    selectedEstimate,
    updateCurrentState,
  ]);

  const onSearch = () => {
    if (searchText) {
      setFilters({
        ...filters,
        searchValue: searchText,
        pageNumber: 0,
      });
    }
  };

  useEffect(() => {
    if (!searchText) {
      setFilters({ ...initialFilters });
    }
  }, [searchText]);

  const deleteSolarEstimation = (estimationId: string) => {
    triggerDeleteSolarEstimation({ estimationId })
      .unwrap()
      .then(() => {
        showSuccessMessage("Solar estimation deleted successfully");
        setUpdateCurrentState((ps) => !ps);
        setShowDeleteSolarEstimationModal(false);
      })
      .catch(() => {
        showErrorMessage("Failed to delete solar estimation");
      });
  };

  const duplicateSolarEstimation = (estimationId: string) => {
    triggerDuplicateEstimation({ estimationId })
      .unwrap()
      .then(() => {
        showSuccessMessage("Solar estimation duplicated successfully");
        setUpdateCurrentState((ps) => !ps);
        setShowDuplicateSolarEstimationModal(false);
      })
      .catch(() => {
        showErrorMessage("Failed to duplicate solar estimation");
      });
  };

  const onFilter = (startDate: string, endDate: string) => {
    setFilters({
      ...filters,
      from: startDate,
      to: endDate,
      pageNumber: 0,
    });
  };

  useEffect(() => {
    triggerSolarEstimationSummary()
      .unwrap()
      .then((res: IViewSolarEstimationSummaryResponseDTO) => {
        setEstimationSummary(res);
      })
      .catch(() => {
        setEstimationSummary({} as IViewSolarEstimationSummaryResponseDTO);
      });
  }, [triggerSolarEstimationSummary]);

  return (
    <>
      {selectedEstimate.value === EstimationStatus.MY_ESTIMATIONS ? (
        <AdminEstimations
          selectedEstimate={selectedEstimate}
          setSelectedEstimate={setSelectedEstimate}
        />
      ) : (
        <>
          <Row className="gy-3 align-items-center justify-content-between">
            <Col className="col-12 col-lg">
              <Row className="gy-3">
                <Col className="col-12 col-sm-6 col-lg-4 col-xl-3">
                  <Search
                    onSearch={onSearch}
                    handleSearchChange={(searchText) =>
                      setSearchText(searchText)
                    }
                    placeholder="Search Systems"
                    value={searchText}
                  />
                </Col>
                <Col className="col-6 col-lg-auto d-none d-sm-block">
                  <AppButton
                    text="New Estimation"
                    className="font-size-14"
                    onClick={() => {
                      localStorage.removeItem(
                        ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED
                      );
                      localStorage.removeItem(
                        ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED
                      );
                      localStorage.removeItem(
                        ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT
                      );
                      localStorage.removeItem(
                        ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER
                      );
                      localStorage.removeItem("simplifiedSave");
                      localStorage.removeItem("directSave");
                      localStorage.removeItem("waterHeaterSave");
                      navigate(AppRoute.DESIGN_SYSTEM);
                    }}
                  />
                </Col>
                {profile?.role === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN && (
                  <Col className="col col-sm col-lg-4 col-xl-3">
                    <AppSelect
                      options={estimateOptions}
                      selectedValue={selectedEstimate}
                      onChangeOption={(selectOption) => {
                        setSelectedEstimate(selectOption);
                      }}
                      className="text-light font-size-14"
                    />
                  </Col>
                )}
                {profile?.role === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN && (
                  <Col className="col-auto d-block d-lg-none">
                    <ButtonWithBadge
                      badgeValue={0}
                      text="Filter"
                      icon="filter_alt"
                      onClick={() => setShowEstimationFilterSidePanel(true)}
                    />
                  </Col>
                )}
              </Row>
            </Col>
            {profile?.role === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN && (
              <Col className="col-auto d-none d-lg-block">
                <ButtonWithBadge
                  badgeValue={0}
                  text="Filter"
                  icon="filter_alt"
                  onClick={() => setShowEstimationFilterSidePanel(true)}
                />
              </Col>
            )}
          </Row>
          <Row className="mt-3 d-block d-sm-none">
            <Col>
              <AppButton
                text={
                  profile?.role === UserRole.ROLE_USER
                    ? "New Estimation"
                    : "Solar Estimator"
                }
                className="font-size-14"
                onClick={() => {
                  localStorage.removeItem(
                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED
                  );
                  localStorage.removeItem(
                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED
                  );
                  localStorage.removeItem(
                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT
                  );
                  localStorage.removeItem(
                    ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER
                  );
                  localStorage.removeItem("simplifiedSave");
                  localStorage.removeItem("directSave");
                  localStorage.removeItem("waterHeaterSave");
                  navigate(AppRoute.DESIGN_SYSTEM);
                }}
              />
            </Col>
          </Row>
          {profile?.role === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN && (
            <Row className="mx-0 mt-4">
              <EstimationSummary
                data={[
                  {
                    count: estimationSummary?.totalEstimations || 0,
                    title: "All Estimations",
                    icon: "add_circle" as MtIcon,
                    iconColor: "#d9b319",
                    onClick: () => {
                      setSelectedEstimate(estimateOptions[0]);
                    },
                    selected: selectedEstimate.value === "",
                    isLoading: isFetchingSolarEstimationSummary,
                  },
                  {
                    count: estimationSummary?.pendingApproval || 0,
                    title: "Waiting Approval",
                    icon: "check" as MtIcon,
                    iconColor: "#22C55E",
                    onClick: () => {
                      setSelectedEstimate(estimateOptions[1]);
                    },
                    selected:
                      selectedEstimate.value === EstimationStatus.SUBMITTED,
                    isLoading: isFetchingSolarEstimationSummary,
                  },
                  {
                    count: estimationSummary?.purchased || 0,
                    title: "Purchased",
                    icon: "remove_shopping_cart" as MtIcon,
                    iconColor: "#38BDF8",
                    onClick: () => {
                      setSelectedEstimate(estimateOptions[5]);
                    },
                    selected:
                      selectedEstimate.value === EstimationStatus.PURCHASED,
                    isLoading: isFetchingSolarEstimationSummary,
                  },
                  {
                    count: estimationSummary?.delayed || 0,
                    title: "Delayed",
                    icon: "asterisk" as MtIcon,
                    iconColor: "#EF4444",
                    onClick: () => {
                      setSelectedEstimate(estimateOptions[6]);
                    },
                    selected:
                      selectedEstimate.value === EstimationStatus.DELAYED,
                    isLoading: isFetchingSolarEstimationSummary,
                  },
                ]}
              />
            </Row>
          )}
          <div
            className={`${
              estimations.length > 0 ? `estimation-container pb-4` : `bg-none`
            } mt-4 rounded-2 position-relative`}
          >
            {estimations && estimations.length > 0 ? (
              <>
                <Row className="px-2">
                  <Col>
                    {profile?.role === UserRole.ROLE_SOLAR_DASHBOARD_ADMIN ? (
                      <DataTable
                        headers={[
                          { id: 1, columnName: "" },
                          {
                            id: 2,
                            columnName: "Estimate No.",
                          },
                          {
                            id: 3,
                            columnName: "Created By",
                          },
                          {
                            id: 4,
                            columnName: "Date",
                          },
                          {
                            id: 5,
                            columnName: "Phone",
                          },
                          {
                            id: 6,
                            columnName: "Email",
                          },
                          {
                            id: 7,
                            columnName: "Type",
                          },
                          {
                            id: 8,
                            columnName: "Energy Need",
                          },
                          {
                            id: 9,
                            columnName: "Survey Required",
                          },
                          {
                            id: 10,
                            columnName: "",
                          },
                          {
                            id: 11,
                            columnName: "",
                          },
                        ]}
                        data={estimations.map((i) => {
                          return {
                            data: [
                              <>
                                {i.isDelayed && (
                                  <MaterialIcon
                                    icon="asterisk"
                                    color="#EF4444"
                                    size={18}
                                    style={{ width: "1px" }}
                                  />
                                )}
                              </>,
                              <>
                                <div>
                                  {i.estimationId || "-"}&nbsp;
                                  {i.title ? `| ${i.title}` : ``}
                                </div>
                              </>,
                              <>
                                {i.user?.firstName}&nbsp;{i.user?.lastName}
                              </>,
                              <>{moment(i.createdAt).format("DD/MM/YYYY")}</>,
                              <>{i.user?.contactNumber || "-"}</>,
                              <>{i.user?.email || "-"}</>,
                              <>
                                {i.estimationType ===
                                ESolarSolutionType.BOTH ? (
                                  <Row className="row-cols-auto align-items-center gx-1">
                                    <Col>
                                      <MaterialIcon
                                        icon={
                                          getSolarEstimationTypeIcon(
                                            i.estimationType
                                          ).solarPower as MtIcon
                                        }
                                        color="var(--color-system-icon)"
                                        fill
                                        size={18}
                                      />
                                    </Col>
                                    <Col className="font-weight-500 title-2">
                                      +
                                    </Col>
                                    <Col>
                                      <MaterialIcon
                                        icon={
                                          getSolarEstimationTypeIcon(
                                            i.estimationType
                                          ).waterDrop as MtIcon
                                        }
                                        color="var(--color-system-icon)"
                                        fill
                                        size={18}
                                      />
                                    </Col>
                                  </Row>
                                ) : (
                                  <MaterialIcon
                                    icon={
                                      i.estimationType ===
                                      ESolarSolutionType.SOLAR_ENERGY_SYSTEM
                                        ? (getSolarEstimationTypeIcon(
                                            i.estimationType
                                          ).solarPower as MtIcon)
                                        : (getSolarEstimationTypeIcon(
                                            i.estimationType
                                          ).waterDrop as MtIcon)
                                    }
                                    color="var(--color-system-icon)"
                                    fill
                                    size={18}
                                  />
                                )}
                              </>,
                              <>
                                {i.data?.inputParams?.monthlyRequiredEnergy
                                  ? `${Number(
                                      Helper.roundTo2(
                                        i.data.inputParams.monthlyRequiredEnergy
                                      )
                                    )} kWh`
                                  : "-"}
                              </>,
                              <>{i.installationCostRequested ? `Yes` : `No`}</>,
                              <>
                                <Row>
                                  <Col
                                    className={`col-auto ${getStatusColor(
                                      i.status
                                    )} pe-4 py-1 rounded-5`}
                                  >
                                    {getStatusNameAdmin(i.status)}
                                  </Col>
                                </Row>
                              </>,
                              <>
                                <Row className="mx-0 align-items-center">
                                  <Col className="pe-3">
                                    <MaterialIcon
                                      icon="visibility"
                                      color="var(--color-visibility-icon)"
                                      size={15}
                                      weight={500}
                                      className="cursor-pointer"
                                      onClick={() =>
                                        navigate(
                                          AppRoute.ESTIMATIONS + "/" + i.id,
                                          {
                                            state: {
                                              estimationId: i.id,
                                              isUserView: false,
                                            },
                                          }
                                        )
                                      }
                                    />
                                  </Col>
                                  <Col className="px-0">
                                    <AppDropDown
                                      items={[
                                        {
                                          text: "Edit Estimation",
                                          onClick: () => {
                                            navigate(
                                              AppRoute.EDIT_ESTIMATIONS +
                                                "/" +
                                                i.id,
                                              {
                                                state: {
                                                  estimationId: i.id,
                                                },
                                              }
                                            );
                                          },
                                          disabled:
                                            i.status ===
                                              EstimationStatus.APPROVED ||
                                            i.status ===
                                              EstimationStatus.APPROVED_WITH_CHANGES ||
                                            i.status ===
                                              EstimationStatus.REJECTED ||
                                            i.status ===
                                              EstimationStatus.PURCHASED,
                                        },
                                        {
                                          text: "Export",
                                          onClick: () => {
                                            setShowEstimationSummaryExportModal(
                                              true
                                            );
                                            setEstimationId(i.id);
                                          },
                                        },
                                        {
                                          text: "Timeline",
                                          onClick: () => {
                                            setShowEstimationTimelineModal(
                                              true
                                            );
                                            setEstimationId(i.id);
                                          },
                                        },
                                        // {
                                        //   text: "Purchase History",
                                        //   onClick: () => {
                                        //     setShowPurchaseHistoryModal(true);
                                        //     setUserId(i.user?.id || "");
                                        //   },
                                        // },
                                      ]}
                                    />
                                  </Col>
                                </Row>
                              </>,
                            ],
                          };
                        })}
                      />
                    ) : (
                      <DataTable
                        headers={[
                          {
                            id: 1,
                            columnName: "Estimate No.",
                          },
                          {
                            id: 2,
                            columnName: "Title",
                          },
                          {
                            id: 3,
                            columnName: "Date",
                          },
                          {
                            id: 4,
                            columnName: "Type",
                          },
                          {
                            id: 5,
                            columnName: "Energy Need",
                          },
                          {
                            id: 6,
                            columnName: "",
                          },
                        ]}
                        data={estimations.map((i) => {
                          return {
                            data: [
                              <>{i.estimationId || "-"}</>,
                              <>{i.title || "-"}</>,
                              <>{moment(i.createdAt).format("DD/MM/YYYY")}</>,
                              <>
                                {i.estimationType ===
                                ESolarSolutionType.BOTH ? (
                                  <Row className="row-cols-auto align-items-center gx-1">
                                    <Col>
                                      <MaterialIcon
                                        icon={
                                          getSolarEstimationTypeIcon(
                                            i.estimationType
                                          ).solarPower as MtIcon
                                        }
                                        color="var(--color-system-icon)"
                                        fill
                                        size={18}
                                      />
                                    </Col>
                                    <Col className="font-weight-500 title-2">
                                      +
                                    </Col>
                                    <Col>
                                      <MaterialIcon
                                        icon={
                                          getSolarEstimationTypeIcon(
                                            i.estimationType
                                          ).waterDrop as MtIcon
                                        }
                                        color="var(--color-system-icon)"
                                        fill
                                        size={18}
                                      />
                                    </Col>
                                  </Row>
                                ) : (
                                  <MaterialIcon
                                    icon={
                                      i.estimationType ===
                                      ESolarSolutionType.SOLAR_ENERGY_SYSTEM
                                        ? (getSolarEstimationTypeIcon(
                                            i.estimationType
                                          ).solarPower as MtIcon)
                                        : (getSolarEstimationTypeIcon(
                                            i.estimationType
                                          ).waterDrop as MtIcon)
                                    }
                                    color="var(--color-system-icon)"
                                    fill
                                    size={18}
                                  />
                                )}
                              </>,
                              <>
                                <Row>
                                  <Col className="col-5">
                                    {i.data?.inputParams?.monthlyRequiredEnergy
                                      ? `${Number(
                                          Helper.roundTo2(
                                            i.data.inputParams
                                              .monthlyRequiredEnergy
                                          )
                                        )} kWh`
                                      : "-"}
                                  </Col>
                                  <Col
                                    className={`col-auto ${
                                      i.isDraft
                                        ? "status-draft status-pending-background"
                                        : getStatusColor(i.status)
                                    } px-3 py-1 rounded-5`}
                                  >
                                    {i.isDraft
                                      ? `Draft`
                                      : getStatusNameUser(i.status)}
                                  </Col>
                                </Row>
                              </>,
                              <>
                                <Row className="mx-0 align-items-center">
                                  <Col className="px-0">
                                    <MaterialIcon
                                      icon="visibility"
                                      color="var(--color-visibility-icon)"
                                      size={15}
                                      weight={500}
                                      className="cursor-pointer"
                                      onClick={() =>
                                        navigate(
                                          AppRoute.ESTIMATIONS + "/" + i.id,
                                          {
                                            state: {
                                              estimationId: i.id,
                                              isUserView: true,
                                            },
                                          }
                                        )
                                      }
                                    />
                                  </Col>
                                  <Col className="px-0">
                                    <AppDropDown
                                      items={[
                                        {
                                          text: "Delete Estimation",
                                          onClick: () => {
                                            setShowDeleteSolarEstimationModal(
                                              true
                                            );
                                            setEstimationId(i.id);
                                          },
                                          disabled:
                                            i.status ===
                                              EstimationStatus.APPROVED ||
                                            i.status ===
                                              EstimationStatus.APPROVED_WITH_CHANGES ||
                                            i.status ===
                                              EstimationStatus.PURCHASED ||
                                            i.status ===
                                              EstimationStatus.SUBMITTED ||
                                            i.status ===
                                              EstimationStatus.MODIFIED,
                                        },
                                        {
                                          text: "Edit Estimation",
                                          onClick: () => {
                                            if (
                                              i.solarEstimationType ===
                                              EEstimationType.SIMPLIFIED
                                            ) {
                                              localStorage.setItem(
                                                ESolarSimLocalStorage.ESTIMATOR_DETAILS_SIMPLIFIED,
                                                JSON.stringify({
                                                  estimationId: i.id,
                                                  estimationToken: "token",
                                                })
                                              );

                                              navigate(
                                                AppRoute.DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM
                                              );
                                              return;
                                            }

                                            if (
                                              i.solarEstimationType ===
                                              EEstimationType.DIRECT
                                            ) {
                                              localStorage.setItem(
                                                ESolarSimLocalStorage.ESTIMATOR_DETAILS_DIRECT,
                                                JSON.stringify({
                                                  estimationId: i.id,
                                                  estimationToken: "token",
                                                })
                                              );

                                              navigate(
                                                AppRoute.DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM
                                              );
                                              return;
                                            }

                                            if (
                                              i.solarEstimationType ===
                                              EEstimationType.ADVANCED
                                            ) {
                                              localStorage.setItem(
                                                ESolarSimLocalStorage.ESTIMATOR_DETAILS_ADVANCED,
                                                JSON.stringify({
                                                  estimationId: i.id,
                                                  estimationToken: "token",
                                                })
                                              );

                                              navigate(
                                                AppRoute.DESIGN_SYSTEM_ADVANCED_DEVICES,
                                                {
                                                  state: {
                                                    isNavigateToAssembleSystem:
                                                      true,
                                                  },
                                                }
                                              );
                                              return;
                                            }

                                            localStorage.setItem(
                                              ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
                                              JSON.stringify({
                                                estimationId: i.id,
                                                estimationToken: "token",
                                              })
                                            );

                                            navigate(
                                              AppRoute.DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM
                                            );
                                          },
                                          disabled:
                                            i.status ===
                                              EstimationStatus.PURCHASED ||
                                            i.status ===
                                              EstimationStatus.SUBMITTED ||
                                            i.status ===
                                              EstimationStatus.MODIFIED,
                                        },
                                        {
                                          text: "Export",
                                          onClick: () => {
                                            setShowEstimationSummaryExportModal(
                                              true
                                            );
                                            setEstimationId(i.id);
                                          },
                                        },
                                        {
                                          text: "Duplicate",
                                          onClick: () => {
                                            setShowDuplicateSolarEstimationModal(
                                              true
                                            );
                                            setEstimationId(i.id);
                                          },
                                        },
                                        // {
                                        //   text: "Purchase History",
                                        //   onClick: () => {
                                        //     setShowPurchaseHistoryModal(true);
                                        //   },
                                        // },
                                      ]}
                                    />
                                  </Col>
                                </Row>
                              </>,
                            ],
                          };
                        })}
                      />
                    )}
                  </Col>
                </Row>
                <Row className="row-cols-auto align-items-center justify-content-center">
                  <Col>
                    <Pagination
                      itemsPerPage={filters.pageSize ?? 0}
                      length={totalElements}
                      currentPage={filters.pageNumber + 1}
                      updateCurrentPage={(pn) => {
                        setFilters((prevFilters) => ({
                          ...prevFilters,
                          pageNumber: pn - 1,
                        }));
                      }}
                    />
                  </Col>
                  <Col className="mt-4">
                    <div
                      className={`country-bg border-2 border-gray-15 rounded-2 d-flex align-items-center justify-content-center text-white py-1 px-3`}
                    >
                      <Row className="align-items-center">
                        <Col className="title-4 font-weight-400 font-size-14 col-auto pe-0">
                          {filters.pageSize} items per page
                        </Col>
                        <Col className="ps-0">
                          <AppDropDown
                            items={[
                              {
                                onClick: () =>
                                  setFilters({
                                    pageNumber: 0,
                                    pageSize: 10,
                                  }),
                                text: "10 items per page",
                              },
                              {
                                onClick: () =>
                                  setFilters({
                                    pageNumber: 0,
                                    pageSize: 20,
                                  }),
                                text: "20 items per page",
                              },
                              {
                                onClick: () =>
                                  setFilters({
                                    pageNumber: 0,
                                    pageSize: 50,
                                  }),
                                text: "50 items per page",
                              },
                            ]}
                            icon="arrow_drop_down"
                            showBackgroundAndBorder={false}
                          />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </>
            ) : (
              <div className="container-dash mt-4 text-center text-color-2 font-size-12">
                There are no estimations
              </div>
            )}
            <SpinnerModal
              show={isFetchingSolarEstimation || isFetchingSolarEstimationAdmin}
            />
          </div>
          <EstimationDeleteModal
            show={showDeleteSolarEstimationModal}
            onCancel={() => setShowDeleteSolarEstimationModal(false)}
            onClose={() => setShowDeleteSolarEstimationModal(false)}
            onConfirm={() => {
              if (estimationId) {
                deleteSolarEstimation(estimationId);
              }
            }}
            isLoading={isLoadingDeleteSolarEstimation}
          />
          <EstimationTimelineModal
            show={showEstimationTimelineModal}
            onCancel={() => setShowEstimationTimelineModal(false)}
            onClose={() => setShowEstimationTimelineModal(false)}
            estimationId={estimationId}
          />
          <EstimationSummaryExportModal
            show={showEstimationSummaryExportModal}
            onCancel={() => setShowEstimationSummaryExportModal(false)}
            onClose={() => setShowEstimationSummaryExportModal(false)}
            estimationId={estimationId}
          />
          <PurchaseHistoryModal
            show={showPurchaseHistoryModal}
            onCancel={() => setShowPurchaseHistoryModal(false)}
            onClose={() => setShowPurchaseHistoryModal(false)}
            userId=""
          />
          <DuplicateEstimationModal
            show={showDuplicateSolarEstimationModal}
            onCancel={() => setShowDuplicateSolarEstimationModal(false)}
            onClose={() => setShowDuplicateSolarEstimationModal(false)}
            onConfirm={() => {
              if (estimationId) {
                duplicateSolarEstimation(estimationId);
              }
            }}
            isLoading={isFetchingDuplicateEstimation}
          />
          <EstimationFilterSidePanel
            showFilter={showEstimationFilterSidePanel}
            onClose={() => setShowEstimationFilterSidePanel(false)}
            onFilter={onFilter}
          />
        </>
      )}
    </>
  );
};

export default Estimations;
